import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import IconButton from '@mui/material/IconButton';
import Slider from '@mui/material/Slider';
import { alpha } from '@mui/material/styles';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { DeleteIcon, EditIcon } from 'assets/icons';
import { ContactButton } from 'components/UI/Buttons';
import { type FC, useState } from 'react';
import { useDeleteSectionMutation, useUpdateSectionMutation } from 'services/api/reports.api';
import type { ReportSection } from 'services/types/report';
import { NAVY, RADIUS_CARD, RADIUS_COIN, RADIUS_ITEM, SHADOW, SURFACE_CONTAINER_LOWEST } from 'styles/tokens';
import textToReactNodes from 'utils/textToReactNodes';

interface Props {
  section: ReportSection;
  reportId: number;
  isEditMode: boolean;
}

const cardSx = {
  p: 3,
  height: '100%',
  position: 'relative',
  bgcolor: SURFACE_CONTAINER_LOWEST,
  borderRadius: RADIUS_CARD,
  boxShadow: SHADOW,
} as const;

const hiddenCardSx = {
  ...cardSx,
  opacity: 0.5,
  border: `2px dashed ${alpha(NAVY, 0.2)}`,
} as const;

const editBtnSx = {
  color: alpha(NAVY, 0.55),
  transition: 'all 0.15s ease-in-out',
  '&:hover': { bgcolor: alpha(NAVY, 0.06) },
} as const;

const inputSx = {
  '& .MuiOutlinedInput-root': {
    borderRadius: RADIUS_ITEM,
    '& fieldset': { borderColor: alpha(NAVY, 0.12) },
    '&:hover fieldset': { borderColor: alpha(NAVY, 0.25) },
  },
} as const;

const TextSection: FC<Props> = ({ section, reportId, isEditMode }: Props) => {
  const [updateSection] = useUpdateSectionMutation();
  const [deleteSection] = useDeleteSectionMutation();

  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editTitle, setEditTitle] = useState(section.title || '');
  const [editSubtitle, setEditSubtitle] = useState(section.subtitle || '');
  const [editText, setEditText] = useState(section.text || '');
  const [editWidth, setEditWidth] = useState(section.width);
  const [editHeight, setEditHeight] = useState(section.height);

  const handleOpenEdit = () => {
    setEditTitle(section.title || '');
    setEditSubtitle(section.subtitle || '');
    setEditText(section.text || '');
    setEditWidth(section.width);
    setEditHeight(section.height);
    setEditDialogOpen(true);
  };

  const handleSaveEdit = () => {
    updateSection({
      reportId,
      sectionId: section.id,
      title: editTitle || null,
      subtitle: editSubtitle || null,
      text: editText || null,
      width: editWidth,
      height: editHeight,
    })
      .unwrap()
      .then(() => setEditDialogOpen(false))
      .catch(console.error);
  };

  const handleToggleVisibility = () => {
    updateSection({ reportId, sectionId: section.id, isHidden: !section.isHidden }).catch(console.error);
  };

  const handleDelete = () => {
    if (!window.confirm('¿Estás seguro de que quieres eliminar esta sección?')) return;
    deleteSection({ reportId, sectionId: section.id }).catch(console.error);
  };

  const isOnlyTitle = !section.subtitle && !section.text;

  return (
    <Box sx={section.isHidden ? hiddenCardSx : cardSx}>
      {isEditMode && (
        <Box sx={{ position: 'absolute', top: 8, right: 8, display: 'flex', gap: 0.5 }}>
          <Tooltip title={section.isHidden ? 'Mostrar sección' : 'Ocultar sección'}>
            <IconButton size="small" onClick={handleToggleVisibility} sx={editBtnSx}>
              {section.isHidden ? <VisibilityOffIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}
            </IconButton>
          </Tooltip>
          <Tooltip title="Editar sección">
            <IconButton size="small" onClick={handleOpenEdit} sx={editBtnSx}>
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Eliminar sección">
            <IconButton
              size="small"
              onClick={handleDelete}
              sx={{ ...editBtnSx, '&:hover': { bgcolor: 'error.light', color: 'error.dark' } }}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      )}
      {section.title && (
        <Typography
          variant={isOnlyTitle ? 'h5' : 'h6'}
          gutterBottom
          sx={{
            fontWeight: 600,
            color: NAVY,
            pr: isEditMode ? 12 : 0,
            textAlign: isOnlyTitle ? 'center' : 'left',
            mb: isOnlyTitle ? 0 : 1,
          }}
        >
          {section.title.replace(/^\*+|\*+$/g, '')}
        </Typography>
      )}
      {section.subtitle && (
        <Typography variant="subtitle1" gutterBottom sx={{ color: alpha(NAVY, 0.6) }}>
          {section.subtitle}
        </Typography>
      )}
      {section.text && (
        <Box sx={{ lineHeight: 1.7, color: NAVY, '& a': { color: 'primary.main' } }}>
          {textToReactNodes(section.text)}
        </Box>
      )}
      {!section.title && !section.subtitle && !section.text && (
        <Typography sx={{ color: alpha(NAVY, 0.4) }}>Sección vacía</Typography>
      )}

      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ color: NAVY, fontWeight: 600, fontFamily: 'Jost, sans-serif' }}>
          Editar sección de texto
        </DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Título"
            fullWidth
            value={editTitle}
            onChange={(e) => setEditTitle(e.target.value)}
            sx={inputSx}
          />
          <TextField
            margin="dense"
            label="Subtítulo"
            fullWidth
            value={editSubtitle}
            onChange={(e) => setEditSubtitle(e.target.value)}
            sx={inputSx}
          />
          <TextField
            margin="dense"
            label="Texto (soporta markdown)"
            fullWidth
            multiline
            rows={8}
            value={editText}
            onChange={(e) => setEditText(e.target.value)}
            sx={inputSx}
          />
          <Box sx={{ mt: 3 }}>
            <Typography gutterBottom sx={{ color: alpha(NAVY, 0.6) }}>
              Ancho: {Math.round((editWidth / 12) * 100)}%
            </Typography>
            <Slider
              value={editWidth}
              onChange={(_, v) => setEditWidth(v as number)}
              min={1}
              max={12}
              step={1}
              marks={[
                { value: 3, label: '25%' },
                { value: 6, label: '50%' },
                { value: 9, label: '75%' },
                { value: 12, label: '100%' },
              ]}
              valueLabelDisplay="auto"
            />
          </Box>
          <Box sx={{ mt: 2 }}>
            <Typography gutterBottom sx={{ color: alpha(NAVY, 0.6) }}>
              Alto: {editHeight}
            </Typography>
            <Slider
              value={editHeight}
              onChange={(_, v) => setEditHeight(v as number)}
              min={1}
              max={4}
              step={1}
              marks={[
                { value: 1, label: '1' },
                { value: 2, label: '2' },
                { value: 3, label: '3' },
                { value: 4, label: '4' },
              ]}
              valueLabelDisplay="auto"
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Button
            onClick={() => setEditDialogOpen(false)}
            sx={{ color: alpha(NAVY, 0.6), borderRadius: RADIUS_COIN, '&:hover': { bgcolor: alpha(NAVY, 0.06) } }}
          >
            Cancelar
          </Button>
          <ContactButton onClick={handleSaveEdit} sx={{ height: 36 }}>
            Guardar
          </ContactButton>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default TextSection;
